import { Card, CardContent, CardDescription, CardHeader, CardTitle } from '@/components/ui/card';
import { Skeleton } from '@/components/ui/skeleton';
import { RefreshCw, AlertCircle, FileText } from 'lucide-react';
import { useTranslation } from 'react-i18next';
import { useDashboardFiltersStore } from '@/store/dashboardFiltersStore';

export interface IdTypeRow {
  idType: string;
  count: number;
}

interface IdTypeChartProps {
  data?: IdTypeRow[];
  loading?: boolean;
  error?: Error;
  height?: number;
  /**
   * Enable the cross-filter behaviour: clicking a row toggles a
   * dashboard-wide filter for that idType. Mirrors the SdkVersionChart
   * pattern.
   */
  enableCrossFilter?: boolean;
}

export function IdTypeChart({
  data,
  loading = false,
  error,
  height = 320,
  enableCrossFilter = false,
}: IdTypeChartProps) {
  const { t } = useTranslation();
  const activeIdType = useDashboardFiltersStore((s) => s.filters.idType);
  const toggleFilter = useDashboardFiltersStore((s) => s.toggleFilter);

  if (loading) {
    return (
      <Card className="h-full flex flex-col">
        <CardHeader>
          <div className="flex items-center gap-2">
            <FileText className="h-5 w-5 text-primary" />
            <CardTitle className="text-base">{t('dashboard.idTypes.title')}</CardTitle>
          </div>
          <CardDescription>{t('dashboard.idTypes.subtitle')}</CardDescription>
        </CardHeader>
        <CardContent className="flex-1 space-y-3">
          {Array.from({ length: 4 }).map((_, i) => (
            <div key={i} className="space-y-2">
              <div className="flex items-center justify-between">
                <Skeleton className="h-4 w-32" />
                <Skeleton className="h-4 w-12" />
              </div>
              <Skeleton className="h-2 w-full rounded-full" />
            </div>
          ))}
        </CardContent>
      </Card>
    );
  }

  if (error) {
    return (
      <Card className="h-full flex flex-col">
        <CardHeader>
          <div className="flex items-center gap-2">
            <FileText className="h-5 w-5 text-primary" />
            <CardTitle className="text-base">{t('dashboard.idTypes.title')}</CardTitle>
          </div>
          <CardDescription>{t('dashboard.idTypes.errorDescription')}</CardDescription>
        </CardHeader>
        <CardContent className="flex-1 flex items-center justify-center" style={{ minHeight: height }}>
          <div className="text-center space-y-3">
            <div className="mx-auto w-12 h-12 bg-gradient-to-br from-destructive/10 to-destructive/5 rounded-xl flex items-center justify-center">
              <AlertCircle className="h-6 w-6 text-destructive/60" />
            </div>
            <div>
              <h3 className="font-medium">{t('dashboard.idTypes.unableToDisplay')}</h3>
              <p className="text-sm text-muted-foreground">{error.message}</p>
            </div>
          </div>
        </CardContent>
      </Card>
    );
  }

  if (!data || data.length === 0) {
    return (
      <Card className="h-full flex flex-col">
        <CardHeader>
          <div className="flex items-center gap-2">
            <FileText className="h-5 w-5 text-primary" />
            <CardTitle className="text-base">{t('dashboard.idTypes.title')}</CardTitle>
          </div>
          <CardDescription>{t('dashboard.idTypes.subtitle')}</CardDescription>
        </CardHeader>
        <CardContent className="flex-1 flex items-center justify-center" style={{ minHeight: height }}>
          <div className="text-center text-muted-foreground">
            <RefreshCw className="h-6 w-6 mx-auto mb-2" />
            <p>{t('dashboard.idTypes.noData')}</p>
          </div>
        </CardContent>
      </Card>
    );
  }

  const sortedData = [...data].sort((a, b) => b.count - a.count);
  const total = sortedData.reduce((sum, row) => sum + row.count, 0);
  const maxCount = sortedData[0].count;

  return (
    <Card className="h-full flex flex-col">
      <CardHeader className="flex-shrink-0">
        <div className="flex items-center gap-2">
          <FileText className="h-5 w-5 text-primary" />
          <CardTitle className="text-base">{t('dashboard.idTypes.title')}</CardTitle>
        </div>
        <CardDescription>
          {t('dashboard.idTypes.totalEvents', {
            count: total,
            uniqueCount: sortedData.length,
          })}
        </CardDescription>
      </CardHeader>
      <CardContent className="flex-1 flex flex-col min-h-0">
        <div className="flex-1 space-y-3 overflow-y-auto" style={{ maxHeight: height }}>
          {sortedData.map((row, index) => {
            const percentage = total > 0 ? (row.count / total) * 100 : 0;
            const barWidth = maxCount > 0 ? (row.count / maxCount) * 100 : 0;
            const isActive = enableCrossFilter && activeIdType === row.idType;
            const rowKey = `${row.idType}|${index}`;
            const body = (
              <>
                <div className="flex items-center justify-between gap-3">
                  <span className="font-mono text-sm truncate min-w-0 flex-1">
                    {row.idType}
                  </span>
                  <div className="text-right flex-shrink-0">
                    <span className="font-semibold text-sm">{row.count.toLocaleString()}</span>
                    <span className="text-xs text-muted-foreground ml-1">
                      ({percentage.toFixed(1)}%)
                    </span>
                  </div>
                </div>
                <div className="w-full bg-muted rounded-full h-1.5">
                  <div
                    className="bg-primary h-1.5 rounded-full transition-all duration-500 ease-out"
                    style={{ width: `${barWidth}%` }}
                  />
                </div>
              </>
            );
            if (!enableCrossFilter) {
              return (
                <div key={rowKey} className="space-y-1.5">
                  {body}
                </div>
              );
            }
            return (
              <button
                type="button"
                key={rowKey}
                onClick={() => toggleFilter('idType', row.idType)}
                aria-pressed={isActive}
                className={`w-full text-left space-y-1.5 p-2 -mx-2 rounded-md border border-transparent transition-colors hover:bg-accent/40 ${
                  isActive ? 'border-primary ring-1 ring-primary/40 bg-primary/5' : ''
                }`}
              >
                {body}
              </button>
            );
          })}
        </div>
      </CardContent>
    </Card>
  );
}
